import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';
import { toast } from 'react-hot-toast';
import './FormsManager.css';

const FormsManager = () => {
  const [forms, setForms] = useState([]);
  const [partners, setPartners] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newForm, setNewForm] = useState({
    name: '',
    source_id: '',
    slug: '',
    description: '',
    partner_id: '',
    is_active: true,
    submit_button_text: 'Submit',
    fields: []
  });
  const [previewSlug, setPreviewSlug] = useState('');

  useEffect(() => {
    loadForms();
    loadPartners();
    loadTemplates();
  }, []);

  const loadForms = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_ENDPOINTS.API_BASE_URL}/api/forms`);
      if (response.data.status === 'success') {
        setForms(response.data.forms);
      }
    } catch (error) {
      console.error('Error loading forms:', error);
      toast.error('Failed to load forms');
    } finally {
      setIsLoading(false);
    }
  };

  const loadPartners = async () => {
    try {
      const response = await axios.get(`${API_ENDPOINTS.API_BASE_URL}/api/partners`);
      setPartners(response.data || []);
    } catch (error) {
      console.error('Error loading partners:', error);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${API_ENDPOINTS.CONTRACTS.DOCUMENTS.SEARCH}?category=general`);
      if (response.data.status === 'success') {
        setTemplates(response.data.templates.filter(t => t.assigned_to === 'template'));
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const handleCreateForm = async (e) => {
    e.preventDefault();
    
    if (!newForm.name || !newForm.source_id) {
      toast.error('Name and Source ID are required');
      return;
    }

    try {
      // Auto-generate slug from name if not provided
      const slug = newForm.slug || newForm.name.toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .replace(/form/gi, '')
        .replace(/^-+|-+$/g, '');
      
      // Default fields for new forms (matches SnapWave form structure)
      const defaultFields = [
        { name: 'First_Legal_Name', label: 'First Legal Name', type: 'text', section: 'Personal Information', required: true, placeholder: 'First Legal Name *' },
        { name: 'Last_Legal_Name', label: 'Last Legal Name', type: 'text', section: 'Personal Information', required: true, placeholder: 'Last Legal Name *' },
        { name: 'Email_Address', label: 'Email Address', type: 'email', section: 'Personal Information', required: true, placeholder: 'Email Address *' },
        { name: 'Phone', label: 'Phone Number', type: 'tel', section: 'Personal Information', required: true, placeholder: 'Phone Number *' },
        { name: 'DOB', label: 'Date of Birth', type: 'dob', section: 'Personal Information', required: true, placeholder: 'Date of Birth (MMDDYYYY) *' },
        { name: 'Preferred_Contact_Method', label: 'Preferred Contact Method', type: 'select', section: 'Personal Information', required: true, placeholder: 'Select contact method *', options: ['WhatsApp', 'iMessage', 'Telegram', 'SMS'] },
        { name: 'IG_Username', label: 'Instagram Username', type: 'text', section: 'Social Media', required: false, placeholder: 'Instagram Username (without @)' },
        { name: 'TT_Username', label: 'TikTok Username', type: 'text', section: 'Social Media', required: false, placeholder: 'TikTok Username (without @)' },
        { name: 'YT_Username', label: 'YouTube Username', type: 'text', section: 'Social Media', required: false, placeholder: 'YouTube Username (without @)' },
        { name: 'Snap_Username', label: 'Snapchat Username', type: 'text', section: 'Social Media', required: false, placeholder: 'Snapchat Username (without @)' },
        { name: 'Want_Snapchat', label: 'Do you want to be on Snapchat?', type: 'select', section: 'Platform Preferences', required: true, placeholder: 'Please select...', options: ['Yes', 'No', 'Unsure'] },
        { name: 'Want_Subscription', label: 'Do you want to do subscription content?', type: 'select', section: 'Platform Preferences', required: true, placeholder: 'Please select...', options: ['Yes', 'No', 'Unsure'] }
      ];
      
      const formData = {
        ...newForm,
        slug: slug,
        fields: defaultFields
      };
      
      const response = await axios.post(`${API_ENDPOINTS.API_BASE_URL}/api/forms`, formData);
      
      if (response.data.status === 'success') {
        toast.success(`Form created! URL: /apply/${slug}`);
        setShowCreateModal(false);
        setNewForm({
          name: '',
          source_id: '',
          slug: '',
          description: '',
          partner_id: '',
          is_active: true,
          submit_button_text: 'Submit',
          fields: []
        });
        loadForms();
      }
    } catch (error) {
      console.error('Error creating form:', error);
      toast.error(error.response?.data?.detail || 'Failed to create form');
    }
  };

  const handleAssignPartner = async (formId, partnerId) => {
    const selectedPartner = partners.find(p => p.id === partnerId);
    
    try {
      const response = await axios.put(
        `${API_ENDPOINTS.API_BASE_URL}/api/forms/${formId}`,
        {
          partner_id: partnerId || null,
          partner_name: selectedPartner?.name || null
        }
      );

      if (response.data.status === 'success') {
        toast.success(`Partner ${partnerId ? 'assigned' : 'removed'}`);
        loadForms();
        
        // Also update all templates assigned to this partner
        if (partnerId) {
          updateTemplatesForPartner(partnerId);
        }
      }
    } catch (error) {
      console.error('Error assigning partner:', error);
      toast.error('Failed to assign partner');
    }
  };

  const updateTemplatesForPartner = async (partnerId) => {
    // This will trigger template re-assignment in the background
    console.log(`Templates for partner ${partnerId} may need updating`);
  };

  const handleDeleteForm = async (formId, formName) => {
    if (!window.confirm(`Are you sure you want to delete "${formName}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API_ENDPOINTS.API_BASE_URL}/api/forms/${formId}`);
      toast.success('Form deleted');
      loadForms();
    } catch (error) {
      console.error('Error deleting form:', error);
      toast.error('Failed to delete form');
    }
  };

  const getPartnerTemplates = (partnerId) => {
    if (!partnerId) return [];
    return templates.filter(t => t.partner_id === partnerId);
  };

  // Generate slug preview as user types
  const generateSlugPreview = (name, customSlug = '') => {
    if (customSlug) {
      return customSlug;
    }
    return name.toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/form/gi, '')
      .replace(/^-+|-+$/g, '');
  };

  // Update preview slug when name or slug changes
  useEffect(() => {
    setPreviewSlug(generateSlugPreview(newForm.name, newForm.slug));
  }, [newForm.name, newForm.slug]);

  return (
    <div className="style-form-mgmt-container">
      <div className="style-form-mgmt-header">
        <h1>📋 Form Management</h1>
        <button
          className="style-form-mgmt-btn style-form-mgmt-btn-primary"
          onClick={() => setShowCreateModal(true)}
        >
          + Create New Form
        </button>
      </div>

      <div className="style-form-mgmt-info">
        <p>
          <strong>How it works:</strong> Create forms, assign them to partners, and assign templates to those partners. 
          When a lead submits a form, the system will automatically use the template assigned to that form's partner.
        </p>
      </div>

      {isLoading ? (
        <div className="style-form-mgmt-loading">Loading forms...</div>
      ) : (
        <div className="style-form-mgmt-forms-grid">
          {forms.map(form => {
            const assignedTemplates = getPartnerTemplates(form.partner_id);
            
            return (
              <div key={form.id} className="style-form-mgmt-form-card">
                <div className="style-form-mgmt-form-card-header">
                  <h3>{form.name}</h3>
                  <span className={`style-form-mgmt-status-badge ${form.is_active ? 'active' : 'inactive'}`}>
                    {form.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div className="style-form-mgmt-form-card-body">
                  <div className="style-form-mgmt-form-field">
                    <label>🔗 Form URL:</label>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      backgroundColor: '#f0f9ff',
                      padding: '12px',
                      borderRadius: '6px',
                      border: '2px solid #0ea5e9'
                    }}>
                      <code style={{ 
                        fontSize: '14px', 
                        fontWeight: 'bold',
                        color: '#0369a1',
                        flex: 1
                      }}>
                        https://track.snapped.cc/apply/{form.slug}
                      </code>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`https://track.snapped.cc/apply/${form.slug}`);
                          toast.success('URL copied!');
                        }}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#0ea5e9',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}
                      >
                        Copy
                      </button>
                    </div>
                  </div>

                  <div className="style-form-mgmt-form-field">
                    <label>Source ID:</label>
                    <code>{form.source_id}</code>
                  </div>

                  <div className="style-form-mgmt-form-field">
                    <label>URL Slug:</label>
                    <code>{form.slug}</code>
                  </div>

                  {form.description && (
                    <div className="style-form-mgmt-form-field">
                      <label>Description:</label>
                      <p>{form.description}</p>
                    </div>
                  )}

                  <div className="style-form-mgmt-form-field">
                    <label>Assigned Partner:</label>
                    <select
                      value={form.partner_id || ''}
                      onChange={(e) => handleAssignPartner(form.id, e.target.value)}
                      className="style-form-mgmt-partner-select"
                    >
                      <option value="">-- No Partner --</option>
                      {partners.map(partner => (
                        <option key={partner.id} value={partner.id}>
                          {partner.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {form.partner_id && (
                    <div className="style-form-mgmt-form-field">
                      <label>Templates for this Partner:</label>
                      {assignedTemplates.length > 0 ? (
                        <ul className="style-form-mgmt-template-list">
                          {assignedTemplates.map(template => (
                            <li key={template.id}>
                              ✅ {template.title}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="style-form-mgmt-no-templates">
                          ⚠️ No templates assigned to {form.partner_name}. 
                          <br />
                          <small>Go to Contract Templates and assign a template to this partner.</small>
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="style-form-mgmt-form-card-footer">
                  <button
                    className="style-form-mgmt-btn style-form-mgmt-btn-danger style-form-mgmt-btn-small"
                    onClick={() => handleDeleteForm(form.id, form.name)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}

          {forms.length === 0 && (
            <div className="style-form-mgmt-no-forms">
              <p>No forms created yet. Click "Create New Form" to get started.</p>
            </div>
          )}
        </div>
      )}

      {/* Create Form Modal */}
      {showCreateModal && (
        <div className="style-form-mgmt-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="style-form-mgmt-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="style-form-mgmt-modal-header">
              <h2>Create New Form</h2>
              <button 
                className="style-form-mgmt-modal-close"
                onClick={() => setShowCreateModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreateForm}>
              <div className="style-form-mgmt-form-group">
                <label>Form Name *</label>
                <input
                  type="text"
                  value={newForm.name}
                  onChange={(e) => setNewForm({...newForm, name: e.target.value})}
                  placeholder="e.g., SnapWave Lead Form"
                  required
                />
              </div>

              {/* URL Preview */}
              {newForm.name && (
                <div className="style-form-mgmt-form-group">
                  <label>🔗 Form URL Preview:</label>
                  <div style={{ 
                    backgroundColor: '#f0f9ff',
                    padding: '12px',
                    borderRadius: '6px',
                    border: '2px solid #0ea5e9',
                    marginBottom: '8px'
                  }}>
                    <code style={{ 
                      fontSize: '14px', 
                      fontWeight: 'bold',
                      color: '#0369a1'
                    }}>
                      https://track.snapped.cc/apply/{previewSlug || '...'}
                    </code>
                  </div>
                  <small style={{ color: '#64748b' }}>
                    This is where users will access your form. You can customize the slug below.
                  </small>
                </div>
              )}

              <div className="style-form-mgmt-form-group">
                <label>Custom URL Slug (optional)</label>
                <input
                  type="text"
                  value={newForm.slug}
                  onChange={(e) => setNewForm({...newForm, slug: e.target.value})}
                  placeholder="Leave blank to auto-generate from name"
                />
                <small>
                  Auto-generated: <strong>{previewSlug || 'Enter a name above'}</strong>
                  <br />
                  Only use lowercase letters, numbers, and hyphens
                </small>
              </div>

              <div className="style-form-mgmt-form-group">
                <label>Source ID *</label>
                <input
                  type="text"
                  value={newForm.source_id}
                  onChange={(e) => setNewForm({...newForm, source_id: e.target.value})}
                  placeholder="e.g., leads_wave"
                  required
                />
                <small>This should match the form source in your call form submissions</small>
              </div>

              <div className="style-form-mgmt-form-group">
                <label>Description</label>
                <textarea
                  value={newForm.description}
                  onChange={(e) => setNewForm({...newForm, description: e.target.value})}
                  placeholder="Brief description of this form"
                  rows="3"
                />
              </div>

              <div className="style-form-mgmt-form-group">
                <label>Assign Partner (optional)</label>
                <select
                  value={newForm.partner_id}
                  onChange={(e) => setNewForm({...newForm, partner_id: e.target.value})}
                >
                  <option value="">-- Select Partner --</option>
                  {partners.map(partner => (
                    <option key={partner.id} value={partner.id}>
                      {partner.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="style-form-mgmt-form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={newForm.is_active}
                    onChange={(e) => setNewForm({...newForm, is_active: e.target.checked})}
                  />
                  {' '}Active
                </label>
              </div>

              <div className="style-form-mgmt-modal-footer">
                <button 
                  type="button" 
                  className="style-form-mgmt-btn style-form-mgmt-btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="style-form-mgmt-btn style-form-mgmt-btn-primary">
                  Create Form
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormsManager;
